import {Helmet} from '@dr.pogodin/react-helmet';
import {skipToken} from '@reduxjs/toolkit/query';
import {DataStatus, Separator, Tabs} from 'platform/components';
import {Box, Grid, GridItem, Hide, Show, VStack} from 'platform/foundation';
import styled from 'styled-components';

import {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {useParams} from 'react-router-dom';

import {isNotNil} from 'ramda';

import {
  selectTenant,
  useGetSourcingAuctionDetailQuery,
  useSourcingVehicleDetailQuery,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {formatDocumentTitle, is404ApiError} from '@dms/shared';

import {useQueryState} from 'shared/web';

import {AdvanceAuctionView} from '../../components/AdvanceAuctionView/AdvanceAuctionView';
import {AdvanceVehicleView} from '../../components/AdvanceVehicleView/AdvanceVehicleView';
import {AuctionPreview} from '../../components/AuctionPreview/AuctionPreview';
import {VehiclePreview} from '../../components/VehiclePreview/VehiclePreview';
import {VehiclePriceForm} from '../../components/VehiclePriceForm/VehiclePriceForm';
import {useAuctionEnumLookup} from '../../hooks/useAuctionEnumLookup';
import {useSourcingCurrency} from '../../hooks/useSourcingCurrency';
import {useSourcingTargetCountry} from '../../hooks/useSourcingTargetCountry';
import {SourcingType, useSourcingType} from '../../hooks/useSourcingType';
import {adaptAuctionVehicleToSourcingItem} from '../../utils/adaptAuctionVehicleToSourcingItem';
import {getDetailFirstLine} from '../../utils/getDetailFirstLine';
import {isEditingTextTarget} from '../../utils/isEditingTextTarget';
import {CommentsTab} from './(sections)/CommentsTab/CommentsTab';
import {CostsTab} from './(sections)/CostsTab/CostsTab';
import {DrillDownTab} from './(sections)/DrillDownTab/DrillDownTab';
import {FullScreenDetailLayout} from './(sections)/FullScreenDetailLayout/FullScreenDetailLayout';
import {HighlightsTab} from './(sections)/HighlightsTab/HighlightsTab';
import {PriceMapTab} from './(sections)/PriceMapTab/PriceMapTab';

const TAB_QUERY_KEY = 'tab';
const DEFAULT_ACTIVE_TAB = 'drillDown';

export function VehicleDetailPage() {
  const {adId} = useParams<{adId?: string}>();
  const sourcingType = useSourcingType();
  const enumLookup = useAuctionEnumLookup();

  const validTabIds: string[] = ['drillDown', 'priceMap', 'costs', 'highlights', 'comments'];
  const [tabIdFromUrl, setTabId] = useQueryState(TAB_QUERY_KEY);
  const tabId =
    tabIdFromUrl && validTabIds.includes(tabIdFromUrl) ? tabIdFromUrl : DEFAULT_ACTIVE_TAB;
  const [fullScreen, setFullScreen] = useState(false);

  const {data: selectedTenant} = useSelector(selectTenant);
  const tenantCountry = selectedTenant?.country;
  const currency = useSourcingCurrency()?.code;
  const country = useSourcingTargetCountry()?.code ?? tenantCountry;

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Backspace') {
        return;
      }
      if (isEditingTextTarget(e.target)) {
        return;
      }
      e.preventDefault();
      window.history.back();
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const {
    data: classifiedsVehicle,
    isLoading: isVehicleLoading,
    isError: isVehicleError,
    error: vehicleError,
  } = useSourcingVehicleDetailQuery(
    isNotNil(adId) ? {externalId: adId, currency, buyerCountry: country} : skipToken,
    {skip: sourcingType === SourcingType.AUCTIONS}
  );
  const {
    data: auctionVehicle,
    isLoading: isAuctionVehicleLoading,
    isError: isAuctionVehicleError,
    error: auctionVehicleError,
  } = useGetSourcingAuctionDetailQuery(
    isNotNil(adId) ? {externalId: adId, currency, buyerCountry: country} : skipToken,
    {skip: sourcingType === SourcingType.CLASSIFIEDS}
  );
  const auctionVehicleAdapted = isNotNil(auctionVehicle)
    ? adaptAuctionVehicleToSourcingItem(auctionVehicle, enumLookup)
    : undefined;
  const vehicle =
    sourcingType === SourcingType.CLASSIFIEDS ? classifiedsVehicle : auctionVehicleAdapted;
  const isLoading =
    sourcingType === SourcingType.CLASSIFIEDS ? isVehicleLoading : isAuctionVehicleLoading;
  const isError =
    sourcingType === SourcingType.CLASSIFIEDS ? isVehicleError : isAuctionVehicleError;
  const error = sourcingType === SourcingType.CLASSIFIEDS ? vehicleError : auctionVehicleError;

  const isVehicleNotFound = is404ApiError(error);
  const errorMessage = isVehicleNotFound
    ? i18n.t('page.vehicle.notifications.notFound')
    : undefined;

  const tabs = [
    {
      id: 'drillDown',
      title: i18n.t('general.labels.drillDown'),
      content: <DrillDownTab vehicle={vehicle} setFullScreen={setFullScreen} />,
    },
    {
      id: 'priceMap',
      title: i18n.t('general.labels.priceMap'),
      content: isNotNil(vehicle) && (
        <PriceMapTab
          vehicle={vehicle}
          data-testid={testIds.sourcing.vehicleDetail('priceMapTab')}
        />
      ),
    },
    {
      id: 'costs',
      title: i18n.t('general.labels.costs'),
      content: isNotNil(adId) && isNotNil(vehicle) && <CostsTab id={adId} vehicle={vehicle} />,
    },
    {
      id: 'highlights',
      title: i18n.t('entity.vehicle.labels.highlighted'),
      content: isNotNil(adId) && isNotNil(vehicle) && (
        <HighlightsTab adId={adId} vehicle={vehicle} />
      ),
    },
    {
      id: 'comments',
      title: i18n.t('page.comments.labels.comments'),
      content: isNotNil(adId) && (
        <CommentsTab
          externalId={adId}
          data-testid={testIds.sourcing.vehicleDetail('commentsTab')}
        />
      ),
    },
  ];

  return (
    <>
      <Helmet
        title={formatDocumentTitle(
          (isNotNil(vehicle) && getDetailFirstLine(vehicle?.sourcingVehicle)) ||
            i18n.t('entity.vehicle.labels.vehicle'),
          i18n.t('page.sourcing.labels.sourcing')
        )}
      />
      <Show when={fullScreen}>
        <FullScreenDetailLayout vehicle={vehicle} onClose={() => setFullScreen(false)} />
      </Show>
      <Hide when={fullScreen}>
        <VStack height="100%">
          <Grid columns={isNotNil(vehicle) ? [1, 1, 3, 3] : 1} spacing={0} grow={1}>
            <GridItem span={isNotNil(vehicle) ? [1, 1, 2, 2] : 1}>
              <DataStatus
                minHeight="100%"
                isLoading={isLoading}
                isError={isError}
                errorMessage={errorMessage}
              >
                {sourcingType === SourcingType.AUCTIONS ? (
                  <AdvanceAuctionView
                    vehicle={auctionVehicleAdapted}
                    isVehicleLoading={isLoading}
                    data-testid={testIds.sourcing.vehicleDetail('advancedAuctionView')}
                  />
                ) : (
                  <AdvanceVehicleView
                    vehicle={vehicle}
                    isVehicleLoading={isLoading}
                    data-testid={testIds.sourcing.vehicleDetail('advancedVehicleView')}
                  />
                )}

                <Box paddingTop={2} paddingHorizontal={4} height="100%">
                  <VehiclePriceForm
                    vehicleId={adId}
                    initialValues={{
                      purchasePrice:
                        vehicle?.vehicleSummary?.purchasePrice?.toString() ?? undefined,
                      sellingPrice: vehicle?.vehicleSummary?.sellingPrice?.toString() ?? undefined,
                      comment: vehicle?.vehicleSummary?.comment ?? undefined,
                    }}
                    data-testid={testIds.sourcing.vehicleDetail('vehiclePriceForm')}
                  />

                  <TabsWrapper>
                    <Tabs
                      tabs={tabs}
                      activeTabId={tabId}
                      onChange={setTabId}
                      data-testid={testIds.sourcing.vehicleDetail('vehicleDetailPage-tabs')}
                    />
                  </TabsWrapper>
                </Box>
              </DataStatus>
            </GridItem>

            <Show when={isNotNil(vehicle)}>
              <VStack minWidth={0}>
                <Hide onNotebook onDesktop>
                  <Separator />
                </Hide>
                {sourcingType === SourcingType.AUCTIONS && isNotNil(auctionVehicle) ? (
                  <AuctionPreview
                    auction={auctionVehicle}
                    data-testid={testIds.sourcing.vehicleDetail('auctionPreview')}
                  />
                ) : (
                  <VehiclePreview vehicleId={adId} isScrollDisabled />
                )}
              </VStack>
            </Show>
          </Grid>
        </VStack>
      </Hide>
    </>
  );
}

// Force Tabs component and all its tabs to have 100% height
// eslint-disable-next-line eag/no-css-property
const TabsWrapper = styled.div`
  height: 100%;

  & > :last-child {
    height: 100%;

    & > * {
      height: 100%;
    }
  }
`;
